import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion as Motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import * as Sentry from '@sentry/react';
import HanziWriter from 'hanzi-writer';
import { useSessionAccumulator } from '../../contexts/SessionAccumulator';
import { selectVariant } from '../../components/delight/MicroVariantEngine';
import InlineXPFloat from '../../components/delight/InlineXPFloat';
import FlawlessXPReward from '../../components/delight/FlawlessXPReward';
import { useLearner } from '../../hooks/useLearner';
import { getRandomBuddyAsset, SLOT } from '../../lib/buddyManifest';
import audioManager from '../../lib/audioManager';
import s from './GameplayScreen.module.css';

// If a freshly-created quiz never finishes loading its stroke data within this
// window, surface the recovery affordance rather than leaving a frozen outline.
// Bundled data resolves in well under this; only a genuine stall trips it.
const LOAD_WATCHDOG_MS = 6000;

/**
 * Gameplay view — one round of up to 5 characters.
 *
 * For each character, HanziWriter runs in .quiz() mode. On completion the
 * result is sent to the backend, micro-feedback fires, and we auto-advance
 * after a short pause. After the last character in the round we call
 * onRoundComplete with the full round results array.
 */
export default function GameplayScreen({
  characters,
  roundNumber,
  recordCharacter,
  enqueueDelight,
  onRoundComplete,
  onQuit,
}) {
  const { t } = useTranslation();
  const { data: activeLearner } = useLearner();
  // Honour reduced-motion for the flawless reward choreography (and to shorten
  // the flawless auto-advance back toward the non-flawless timing). Read from a
  // ref inside the completion callback so its deps stay stable.
  const prefersReducedMotion = useReducedMotion();
  const reduceRef = useRef(prefersReducedMotion);
  reduceRef.current = prefersReducedMotion;
  // Resolve the learner's buddy encouragement pose once for the screen's
  // lifetime — random pick is held, not re-rolled on every stroke error.
  const [encourageSrc] = useState(() =>
    getRandomBuddyAsset(activeLearner?.buddy?.slug, SLOT.ENCOURAGE)
  );
  const [charIndex, setCharIndex] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [mistakeCount, setMistakeCount] = useState(0);
  // Drives the encouragement overlay's show/hide. Separate from mistakeCount
  // (which drives the copy): the buddy appears on every 2nd stroke error and
  // auto-dismisses after 1.5s. See FP-BUDDY-POLISH-01 §B.
  const [encourageVisible, setEncourageVisible] = useState(false);
  // Recovery state (FP-WRITING-QUIZ-FREEZE-01): set when stroke data fails to
  // load (or never loads within the watchdog window) for the current character.
  // Surfaces a Retry / Skip affordance so a quiz can never silently die.
  const [loadError, setLoadError] = useState(false);
  // Bumping this re-runs the writer-init effect to re-attempt the load for the
  // current character without remounting the screen or losing round progress.
  const [reloadKey, setReloadKey] = useState(0);
  // The bundled stroke-data loader is code-split into a practice-only chunk (its
  // char→URL map for the scoped data set is kept out of the main bundle, since
  // PracticePage is not lazy-routed). Dynamically imported on mount; quiz waits.
  const [loaderReady, setLoaderReady] = useState(false);
  const charDataLoaderRef = useRef(null);
  const writerContainerRef = useRef(null);
  const writerRef = useRef(null);
  const watchdogRef = useRef(null);
  const mistakesRef = useRef(0);
  // Errors accumulated since the encouragement last appeared. Resets to 0 each
  // time the buddy shows, so the next appearance again requires 2 more errors.
  const sinceLastShowRef = useRef(0);
  const encourageTimerRef = useRef(null);
  const roundResultsRef = useRef([]);
  const accumulator = useSessionAccumulator();

  const currentChar = characters[charIndex];

  useEffect(() => {
    roundResultsRef.current = [];
  }, [roundNumber]);

  // Clear any pending auto-dismiss timer on unmount.
  useEffect(() => () => clearTimeout(encourageTimerRef.current), []);

  // Writing-practice BGM (audio-system.md). Started on mount; intentionally NOT
  // stopped on unmount — GameplayScreen unmounts between rounds (RoundComplete →
  // next round), so stopping here would gap the music every round. The next BGM
  // screen (map biome, reader, …) crossfades it out; playBGM no-ops if the track
  // is already playing, so re-mounting per round doesn't restart it.
  useEffect(() => {
    audioManager.playBGM('writing-practice', 'writing');
  }, []);

  // A stroke error: bump the mistake total (drives copy + hint), record the
  // miss, and tick the "since last show" counter. Every 2nd error pops the
  // encouragement and restarts its 1.5s auto-dismiss timer.
  const handleMistake = useCallback(() => {
    mistakesRef.current += 1;
    setMistakeCount(mistakesRef.current);
    accumulator.recordMiss();
    audioManager.playSFX('stroke-wrong');

    sinceLastShowRef.current += 1;
    if (sinceLastShowRef.current >= 2) {
      sinceLastShowRef.current = 0;
      setEncourageVisible(true);
      audioManager.playSFX('encourage');
      clearTimeout(encourageTimerRef.current);
      encourageTimerRef.current = setTimeout(() => setEncourageVisible(false), 1500);
    }
  }, [accumulator]);

  const handleCharacterComplete = useCallback(async (charForCompletion, totalMistakes) => {
    const correct = totalMistakes === 0;
    // Character fully written — the "got it!" completion chime (audio-system.md).
    audioManager.playSFX('character-mastered');
    let xpAwarded = 0;
    let variant = 'standard';
    let intensity = 0.5;

    try {
      const response = await recordCharacter({
        characterId: charForCompletion.id,
        correct,
        mistakes: totalMistakes,
        strokeCount: charForCompletion.stroke_count || 1,
      });
      xpAwarded = response?.progression?.xp_awarded || 0;

      const pick = selectVariant({
        consecutiveCorrect: accumulator.consecutiveCorrect,
        isPerfect: correct,
      });
      variant = pick.variant;
      intensity = pick.intensity;

      if (xpAwarded > 0) {
        const rect = writerContainerRef.current?.getBoundingClientRect();
        accumulator.recordEvent({
          xp: xpAwarded,
          variant,
          intensity,
          origin: rect
            ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
            : { x: window.innerWidth / 2, y: window.innerHeight / 3 },
          // Flawless writes get the hero sequence (FlawlessXPReward), which shows
          // the XP itself — suppress the generic float so only one figure shows.
          // Counters (sessionXP / eventCount / streak) still update.
          silent: correct,
        });
      }

      if (response?.progression) {
        enqueueDelight(response.progression);
      }
    } catch (err) {
      console.error('Failed to record character:', err);
    }

    setLastResult({ correct, xp: xpAwarded, variant, intensity });
    setShowResult(true);

    const result = {
      characterId: charForCompletion.id,
      char: charForCompletion.char,
      correct,
      mistakes: totalMistakes,
      xp: xpAwarded,
    };
    roundResultsRef.current = [...roundResultsRef.current, result];

    // Flawless completions hold longer so the full three-beat hero sequence
    // (~1500ms) finishes before the next character mounts; non-flawless keeps
    // the original 1200ms. Reduced motion shortens the flawless hold back toward
    // 1200ms since there's no choreography to wait on.
    const advanceDelay = correct && !reduceRef.current ? 1250 : 1200;
    setTimeout(() => {
      setShowResult(false);
      const nextIndex = charIndex + 1;
      if (nextIndex < characters.length) {
        setCharIndex(nextIndex);
      } else {
        onRoundComplete(roundResultsRef.current);
      }
    }, advanceDelay);
  }, [charIndex, characters.length, recordCharacter, enqueueDelight, accumulator, onRoundComplete]);

  const startQuiz = useCallback(() => {
    const writer = writerRef.current;
    if (!writer || !currentChar) return;
    writer.quiz({
      onMistake: handleMistake,
      onCorrectStroke: () => audioManager.playSFX('stroke-correct'),
      onComplete: () => {
        handleCharacterComplete(currentChar, mistakesRef.current);
      },
    });
  }, [currentChar, handleMistake, handleCharacterComplete]);

  // Dynamically import the bundled stroke-data loader once, into its own chunk.
  // Cached after first load. Reusable for retry if the chunk itself failed.
  const ensureLoaderModule = useCallback(async () => {
    if (charDataLoaderRef.current) return;
    const mod = await import('../../lib/hanziCharData');
    charDataLoaderRef.current = mod.loadHanziCharData;
    setLoaderReady(true);
  }, []);

  useEffect(() => {
    let active = true;
    ensureLoaderModule().catch((err) => {
      if (!active) return;
      console.error('[writing-quiz] failed to load stroke-data module:', err);
      Sentry.captureException(err);
      setLoadError(true);
    });
    return () => {
      active = false;
    };
  }, [ensureLoaderModule]);

  // Initialize HanziWriter quiz for current character
  useEffect(() => {
    if (!currentChar?.char || !writerContainerRef.current) return;
    // Wait for the stroke-data loader chunk before constructing the writer.
    if (!loaderReady || !charDataLoaderRef.current) return;

    const container = writerContainerRef.current;
    container.innerHTML = '';
    mistakesRef.current = 0;
    setMistakeCount(0);
    setShowResult(false);
    setLoadError(false);
    // Fresh character → reset the encouragement counter and hide any open popup.
    sinceLastShowRef.current = 0;
    setEncourageVisible(false);
    clearTimeout(encourageTimerRef.current);
    clearTimeout(watchdogRef.current);

    // Logs char/index/round + reason on any load or init failure so a future
    // recurrence is self-diagnosing (console + Sentry — FP-OBSERVABILITY-01).
    const reportFailure = (reason, detail) => {
      const ctx = {
        char: currentChar.char,
        charIndex,
        roundNumber,
        reason,
        detail: detail instanceof Error ? detail.message : String(detail ?? ''),
      };
      console.error('[writing-quiz] stroke-data failure:', ctx);
      Sentry.captureMessage('writing_quiz_load_failure', {
        level: 'error',
        extra: ctx,
      });
      clearTimeout(watchdogRef.current);
      setLoadError(true);
    };

    let writer;
    try {
      writer = HanziWriter.create(container, currentChar.char, {
        width: 480,
        height: 480,
        padding: 8,
        showOutline: true,
        showCharacter: false,
        showHintAfterMisses: 1,
        highlightOnComplete: true,
        strokeAnimationSpeed: 1,
        strokeHighlightSpeed: 1,
        drawingWidth: 28,
        strokeColor: '#211e1a',
        radicalColor: '#211e1a',
        outlineColor: '#b5ada0',
        drawingColor: '#211e1a',
        highlightColor: '#ff6821',
        // Stroke data resolves from app-bundled static assets (the scoped
        // curriculum-union set), not the third-party CDN HanziWriter defaults to.
        charDataLoader: (char) => charDataLoaderRef.current(char),
        // Data finished loading → quiz is interactive; stand the watchdog down.
        onLoadCharDataSuccess: () => clearTimeout(watchdogRef.current),
        // Data failed to load → surface recovery instead of a frozen outline.
        onLoadCharDataError: (reason) => reportFailure('load_error', reason),
      });
    } catch (err) {
      reportFailure('create_threw', err);
      return () => {
        clearTimeout(watchdogRef.current);
        if (container) container.innerHTML = '';
        writerRef.current = null;
      };
    }

    // HanziWriter doesn't set a viewBox — add one so CSS scaling works
    const svg = container.querySelector('svg');
    if (svg) {
      svg.setAttribute('viewBox', '0 0 480 480');
      svg.removeAttribute('width');
      svg.removeAttribute('height');
    }

    writerRef.current = writer;

    writer.quiz({
      onMistake: handleMistake,
      onCorrectStroke: () => audioManager.playSFX('stroke-correct'),
      onComplete: () => {
        handleCharacterComplete(currentChar, mistakesRef.current);
      },
    });

    // Safety net for a silent stall (partial load / dead instance) where neither
    // onLoadCharDataError nor onComplete ever fires. Never auto-skips or awards
    // XP — it only reveals the user-initiated recovery affordance.
    watchdogRef.current = setTimeout(
      () => reportFailure('load_timeout', `no char data within ${LOAD_WATCHDOG_MS}ms`),
      LOAD_WATCHDOG_MS
    );

    return () => {
      clearTimeout(watchdogRef.current);
      // Fully tear down the previous instance before the next is created, so
      // stale async from the prior character (data XHR, stroke RAF) can't fire
      // against a detached DOM or confuse the new quiz.
      try {
        writer.cancelQuiz();
      } catch {
        /* quiz may already be torn down */
      }
      if (container) container.innerHTML = '';
      writerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChar?.char, charIndex, reloadKey, loaderReady]);

  // Retry the current character: clear the error, re-attempt the loader chunk if
  // it never loaded, and re-run the init effect for the current character.
  const handleRetry = useCallback(() => {
    setLoadError(false);
    if (!charDataLoaderRef.current) {
      ensureLoaderModule().catch((err) => {
        console.error('[writing-quiz] retry: stroke-data module load failed:', err);
        Sentry.captureException(err);
        setLoadError(true);
      });
    }
    setReloadKey((k) => k + 1);
  }, [ensureLoaderModule]);

  // Escape hatch so the user is never stuck: advance past the current character
  // WITHOUT recording it or awarding XP (no fabricated completion). If it was the
  // last character, finish the round with whatever was genuinely traced.
  const handleSkipCharacter = useCallback(() => {
    clearTimeout(watchdogRef.current);
    setLoadError(false);
    setShowResult(false);
    const nextIndex = charIndex + 1;
    if (nextIndex < characters.length) {
      setCharIndex(nextIndex);
    } else {
      onRoundComplete(roundResultsRef.current);
    }
  }, [charIndex, characters.length, onRoundComplete]);

  const handleShowHint = useCallback(() => {
    const writer = writerRef.current;
    if (!writer) return;
    try {
      writer.cancelQuiz();
    } catch {
      /* no-op if quiz already ended */
    }
    mistakesRef.current = 0;
    setMistakeCount(0);
    writer.animateCharacter({
      onComplete: () => {
        startQuiz();
      },
    });
  }, [startQuiz]);

  if (!currentChar) {
    return <div className={s.loading}>{t('practice.preparing')}</div>;
  }

  const showHint = mistakeCount >= 2 && !showResult;
  const encouragementByCount = {
    1: t('practice.encouragementTryAgain'),
    2: t('practice.encouragementGotThis'),
  };
  const encouragement =
    mistakeCount > 0 && !showResult
      ? encouragementByCount[mistakeCount] || t('practice.encouragementLookAgain')
      : null;

  return (
    <div className={s.gameplay}>
      <header className={s.header}>
        <button type="button" className={s.closeButton} onClick={onQuit} aria-label={t('practice.closeAriaLabel')}>
          {t('practice.close')}
        </button>
        <span className={s.roundLabel}>{t('practice.roundLabel', { n: roundNumber })}</span>
        <span className={s.xpBadge} aria-label={t('practice.sessionXpAriaLabel', { xp: accumulator.sessionXP })}>
          {t('practice.xpBadge', { xp: accumulator.sessionXP })}
        </span>
      </header>

      <div className={s.progressDots} role="progressbar" aria-valuemin={0} aria-valuemax={characters.length} aria-valuenow={charIndex}>
        {characters.map((c, i) => (
          <div
            key={c.id}
            className={`${s.dot} ${i <= charIndex ? s.completed : ''}`}
          />
        ))}
      </div>

      <div className={s.practicePanel}>
        <div className={s.charInfo}>
          {currentChar.pinyin && <span className={s.charPinyin}>{currentChar.pinyin}</span>}
          {currentChar.meaning && <span className={s.charMeaning}>{currentChar.meaning}</span>}
        </div>

        <div className={s.canvasWrapper}>
          {/* Beat 1 of the flawless reward: the character the learner drew
              scales up and dissolves into the win. framer-motion animates the
              wrapper element only — HanziWriter internals / freeze-fix loader
              are untouched. Resets to scale 1 / opacity 1 when showResult goes
              false on the next character's mount. Reduced motion: fade only. */}
          <Motion.div
            ref={writerContainerRef}
            className={s.canvas}
            animate={
              showResult && lastResult?.correct
                ? prefersReducedMotion
                  ? { scale: 1, opacity: 0 }
                  : { scale: 1.15, opacity: 0 }
                : { scale: 1, opacity: 1 }
            }
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />

          {showResult && lastResult?.correct && (
            <FlawlessXPReward xp={lastResult.xp} />
          )}
        </div>

        {/* Encouragement overlay — positioned (absolute) inside the writing
            card, so it never shifts layout. Fades in/out via AnimatePresence
            (FP-BUDDY-POLISH-01 §B). Buddy ~130px; copy alongside/above per bp. */}
        <AnimatePresence>
          {encourageVisible && encourageSrc && (
            <Motion.div
              className={s.encouragement}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              aria-live="polite"
            >
              <img src={encourageSrc} alt="" className={s.encouragementBuddy} />
              <span className={s.encouragementBubble}>
                {encouragement || t('practice.encouragementGotThis')}
              </span>
            </Motion.div>
          )}
        </AnimatePresence>

        <div className={s.hintSlot}>
          {loadError ? (
            // Recovery affordance — reuses existing .charMeaning + .showHintButton
            // classes (no module.css change). Retry re-loads + restarts this
            // character's quiz; Skip moves on without recording or awarding XP.
            <>
              <span className={s.charMeaning} role="alert">
                {t('practice.loadErrorMessage')}
              </span>
              <button type="button" className={s.showHintButton} onClick={handleRetry}>
                {t('practice.tryAgain')}
              </button>
              <button type="button" className={s.showHintButton} onClick={handleSkipCharacter}>
                {t('practice.skipCharacter')}
              </button>
            </>
          ) : (
            showHint && (
              <button type="button" className={s.showHintButton} onClick={handleShowHint}>
                {t('practice.showHint')}
              </button>
            )
          )}
        </div>
      </div>

      {accumulator.floats.map((f) => (
        <InlineXPFloat
          key={f.id}
          xp={f.xp}
          variant={f.variant}
          intensity={f.intensity}
          origin={f.origin}
          onComplete={() => accumulator.removeFloat(f.id)}
        />
      ))}
    </div>
  );
}
